/**
 * Method group extracted from the Konva map Stimulus controller.
 * These functions intentionally use `this` so the thin Stimulus adapter can
 * keep the existing target/value/action API while the implementation lives in
 * smaller feature-oriented modules.
 */
import Konva from 'konva';
import {
  DRAW_WIDTH,
  ERASE_RADIUS,
  MAX_FOG_BRUSH_SIZE,
  MIN_FOG_BRUSH_SIZE,
  MAX_SCALE,
  MIN_SCALE,
  MIN_SHAPE_DIMENSION,
  POSITION_ZOOM_STEP,
  SHAPE_COLORS,
  SHAPE_DEFAULTS,
  SHAPE_TRANSFORMER_OPTIONS,
  TOOLBAR_DEFAULT_TOOLS,
  TOOLBAR_TOOLS,
  TOOLBAR_URL_PARAM,
  ZOOM_STEP
} from './constants.js';

export const FogMethods = {
  _syncFogOpacity(konvaLayer = this._fogLayer?.konvaLayer) {
    // Player fog has its own opaque presentation. Editor tool changes only
    // affect the CSS compositing of this canvas, never stored fog geometry.
    if (this.readonlyValue || this.playerValue) return;
    const canvas = konvaLayer?.getCanvas()._canvas;
    if (!canvas) return;
    const editingFog = TOOLBAR_TOOLS.fog.includes(this._tool);
    canvas.style.opacity = String(this.workspaceValue && this._positionManagerActive ? 1 : this.fogOpacityValue * (editingFog ? 1 : 0.5));
    this._fogPresentation?.syncOpacity();
  },

  _clampFogBrushSize(value) {
    const size = Number.isFinite(value) ? Math.round(value) : DRAW_WIDTH;
    return Math.max(MIN_FOG_BRUSH_SIZE, Math.min(MAX_FOG_BRUSH_SIZE, size));
  },

  _currentFogBrushSize() {
    return this._tool === 'erase'
      ? this._clampFogBrushSize(this.eraseBrushSizeValue)
      : this._clampFogBrushSize(this.drawBrushSizeValue);
  },

  _fogBrushCursorRadius() {
    return this._currentFogBrushSize() / 2;
  },

  _updateFogBrushCursorSize() {
    if (!this._cursorCircle) return;
    this._cursorCircle.radius(this._fogBrushCursorRadius());
    this._cursorLayer?.batchDraw();
  },

  _syncFogBrushPopover() {
    const active = this._tool === 'draw' || this._tool === 'erase';
    const anchor = active && this.fogBrushAnchorTargets.find((element) => element.dataset.tool === this._tool);
    if (this.hasFogBrushPopoverTarget) {
      this._setToolbarPopover(this.fogBrushPopoverTarget, this.workspaceValue ? (this._brushDismissed ? null : anchor) : this._mobileEditor ? null : anchor);
    }
    if (!active) return;

    const isErase = this._tool === 'erase';
    const size = this._currentFogBrushSize();
    for (const input of this.fogBrushSizeInputTargets) {
      input.value = size;
      input.setAttribute('aria-label', isErase ? 'Fog eraser size' : 'Fog pencil size');
      input.setAttribute('aria-valuetext', `${size} pixels`);
    }
    if (this.hasMobileBrushLabelTarget)
      this.mobileBrushLabelTarget.textContent = `${isErase ? 'Eraser' : 'Pencil'} size · ${size} px`;
  },

  _onRectDown() {
    const worldPos = this._getWorldPointerPosition();
    if (!worldPos) return;
    this._rectStart = { x: worldPos.x, y: worldPos.y };
    this._painting = true;
    this._initRectPreview(worldPos);
  },

  _initRectPreview(worldPos) {
    if (this._rectPreviewShape) this._rectPreviewShape.destroy();

    const isErase = this._tool === 'erase-rect';
    const isShape = this._tool === 'shape-rect';
    let stroke, fill, dash;

    if (isShape) {
      stroke = this._selectedShapeColor;
      fill = 'transparent';
      dash = [6, 4];
    } else if (isErase) {
      stroke = 'rgba(239,68,68,0.9)';
      fill = 'rgba(239,68,68,0.15)';
    } else {
      stroke = 'rgba(255,255,255,0.9)';
      fill = 'rgba(0,0,0,0.35)';
    }

    this._rectPreviewShape = new Konva.Rect({
      x: worldPos.x,
      y: worldPos.y,
      width: 0,
      height: 0,
      stroke,
      strokeWidth: isShape ? 3 : 2,
      strokeScaleEnabled: false,
      dash,
      fill,
      listening: false
    });
    this._cursorLayer.add(this._rectPreviewShape);
  },

  _updateRectPreview(worldPos) {
    if (!this._rectPreviewShape || !this._rectStart) return;
    this._rectPreviewShape.setAttrs({
      x: Math.min(this._rectStart.x, worldPos.x),
      y: Math.min(this._rectStart.y, worldPos.y),
      width: Math.abs(worldPos.x - this._rectStart.x),
      height: Math.abs(worldPos.y - this._rectStart.y)
    });
    this._cursorLayer.batchDraw();
  },

  _consumeRectPreview() {
    if (!this._rectPreviewShape || !this._rectStart) return null;
    const geo = {
      x: this._rectPreviewShape.x(),
      y: this._rectPreviewShape.y(),
      width: this._rectPreviewShape.width(),
      height: this._rectPreviewShape.height()
    };
    this._rectPreviewShape.destroy();
    this._rectPreviewShape = null;
    this._rectStart = null;
    this._cursorLayer.batchDraw();
    return geo.width < 2 || geo.height < 2 ? null : geo;
  },

  _commitFogRect() {
    const geo = this._consumeRectPreview();
    if (!geo || !this._fogLayer) return;

    this._fogPresentation?.beforeUpdate();
    const isErase = this._tool === 'erase-rect';
    const rect = new Konva.Rect(
      isErase
        ? { ...geo, fill: 'black', globalCompositeOperation: 'destination-out', listening: false }
        : this._fogRectAttrs({ ...geo, globalCompositeOperation: 'source-over' })
    );
    this._fogLayer.group.add(rect);
    this._fogLayer.strokes.push(rect);
    this._fogLayer.konvaLayer.batchDraw();
    this._fogPresentation?.afterUpdate();
  },

  _commitShapeRect() {
    const geo = this._consumeRectPreview();
    if (!geo) return;

    this._shapeCounter++;
    const id = `shape_${crypto.randomUUID()}`;
    const name = `Layer ${this._shapeCounter}`;
    const color = this._selectedShapeColor;
    const aboveFog = SHAPE_DEFAULTS.aboveFog;

    const rect = new Konva.Rect({
      ...geo,
      stroke: color,
      strokeWidth: SHAPE_DEFAULTS.strokeWidth,
      strokeScaleEnabled: false,
      fill: 'transparent',
      listening: false
    });
    const labels = this._createShapeLabels(geo.x, geo.y, geo.width, geo.height, name, color);

    const targetLayer = aboveFog ? this._shapesLayerAbove : this._shapesLayerBelow;
    targetLayer.add(rect);
    targetLayer.add(labels);
    targetLayer.batchDraw();

    this._shapes.push({
      id,
      name,
      color,
      type: 'rect',
      creatorId: this.userIdValue,
      showLabels: true,
      aboveFog,
      strokeWidth: SHAPE_DEFAULTS.strokeWidth,
      fillColor: SHAPE_DEFAULTS.fillColor,
      fillOpacity: SHAPE_DEFAULTS.fillOpacity,
      konvaShape: rect,
      konvaLabels: labels
    });
    this._applyTool('select');
    this._selectShape(id);
    if (!this._usingTouch) this._focusShapeControls(id);
    this._save();
  },

  _addFogPoint() {
    const entry = this._fogLayer;
    if (!entry) return;
    const worldPos = this._getWorldPointerPosition();
    if (!worldPos) return;

    const { x, y } = worldPos;
    const isErase = this._tool === 'erase';
    this._fogPresentation?.beforeUpdate();

    if (!this._lastLine) {
      this._lastLine = new Konva.Line({
        strokeWidth: this._currentFogBrushSize(),
        globalCompositeOperation: isErase ? 'destination-out' : 'source-over',
        lineCap: 'round',
        lineJoin: 'round',
        points: [x, y, x, y]
      });
      this._applyFogLineStroke(this._lastLine, this._lastLine.globalCompositeOperation());
      entry.group.add(this._lastLine);
      entry.strokes.push(this._lastLine);
    } else {
      this._lastLine.points(this._lastLine.points().concat([x, y]));
    }
    entry.konvaLayer.batchDraw();
    this._fogPresentation?.afterUpdate();
  }
};
